import { Matrix, Mesh, MeshBuilder, TransformNode, Vector2, Vector3 } from '@babylonjs/core';
import { Level } from './Level';

import { AdvancedDynamicTexture, Button, Control, Ellipse, Grid, Image, Line, Rectangle, TextBlock } from '@babylonjs/gui';
import { Gui } from './gui/Gui';
import { Gui2D } from './gui/Gui2D';

export class Hud {
  gui2D: Gui2D;
  message: TextBlock;

  time: TextBlock;
  lives: TextBlock;
  score: TextBlock;
  coins: TextBlock;
  ammunition: TextBlock;
  progress: TextBlock;
  airJumps: TextBlock;

  fps: TextBlock;
  background: Image;

  // Pre-allocated pool of animation controls — avoids addControl/dispose churn per collectible
  private static readonly MAX_CONCURRENT_ANIMS = 4;

  private animPool: TextBlock[] = [];
  private animPoolInUse: boolean[] = [];
  private readonly identityMatrix = Matrix.Identity();

  constructor(public level: Level) {
    this.gui2D = this.level.game.gui2D;

    this.createMessage();
    this.initAnimPool();

    //this.createBackground();

    this.time = this.createTextBlock('time');
    this.time.fontWeight = 'bold';
    this.lives = this.createTextBlock('lives');
    this.score = this.createTextBlock('score');
    this.coins = this.createTextBlock('coins');
    this.ammunition = this.createTextBlock('ammunition');
    this.airJumps = this.createTextBlock('airJumps');
    this.fps = this.createTextBlock('fps');
    this.progress = this.createTextBlock('progress');

    const topControls = [this.time, this.lives, this.coins, this.ammunition, this.airJumps, this.score, this.progress];

    if (this.level.game.config.debugFps) {
      topControls.push(this.fps);
    }

    const topGrid = this.level.game.gui2D.createGrid(
      'hud_grid_top',
      '90%',
      '7%',
      1,
      topControls.length,
      10,
      (grid: Grid, row: number, column: number, index: number) => {
        return topControls[index];
      }
    );
    topGrid.top = '-46%';
    topGrid.left = '-2%';

    let cursorBlink = 0;
    let show = true;

    const colorTime = () => {
      if (this.level.timeLimit < this.level.config.timeLimit / 3) {
        return 'red';
      }
      if (this.level.timeLimit < (this.level.config.timeLimit / 3) * 2) {
        return 'orange';
      }
      return Gui.primaryColor.toHexString();
    };

    const refresh = setInterval(() => {
      cursorBlink++;
      if (cursorBlink % 4 === 0) {
        cursorBlink = 0;
        show = !show;
      }

      this.time.text = '⏱️ ' + this.getTimeText(this.level.timeLimit, show);
      this.time.color = colorTime();

      this.lives.text = '❤️ ' + this.level.player.lives.toFixed(0);
      this.coins.text = '💰 ' + this.level.player.coins.toFixed(0);
      this.score.text = '🎯 ' + this.level.player.score.toFixed(0);
      this.ammunition.text = '🍎 ' + this.level.player.ammunition.toFixed(0);
      this.airJumps.text = '💨 ' + this.level.player.maxAirJumps.toFixed(0);
      this.progress.text = '🚀 ' + this.level.player.getProgress().toFixed(0) + '%';

      if (this.level.game.config.debugFps) {
        this.fps.text = '🖥️ ' + this.level.game.engine.getFps().toFixed();
      }
    }, 250);

    this.level.game.scene.onDisposeObservable.add(() => {
      clearInterval(refresh);
    });
  }

  createTextBlock(name: string): TextBlock {
    const textBlock = this.level.game.gui2D.createTextBlock(name, '', undefined, '36%');
    return textBlock;
  }

  createBackground() {
    this.background = new Image('background', 'assets/image/hud.png');
    this.background.width = '100%';
    this.background.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    this.gui2D.ui.addControl(this.background);
  }

  // Set style for big messages
  private setBigMessageStyle() {
    this.message.outlineColor = Gui.secondaryColor.toHexString();
    this.message.outlineWidth = 4;
    this.message.fontSize = '72px';
  }

  // Set style for small messages
  private setSmallMessageStyle() {
    this.message.outlineColor = undefined;
    this.message.outlineWidth = 0;
    this.message.fontSize = '36px';
  }

  // Create a message text block
  private createMessage() {
    this.message = new TextBlock('message');
    this.message.textWrapping = true;
    this.message.width = '100%';
    this.message.height = '100%';
    this.message.color = Gui.primaryColor.toHexString();
    this.message.paddingBottom = '30px';
    this.message.fontSize = Gui.fontSizeL;
    this.message.fontWeight = 'bold';
    this.message.fontFamily = Gui.fontFamily;
    this.message.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    this.message.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    this.gui2D.ui.addControl(this.message);
    this.message.isVisible = false;
  }

  /**
   * Convert seconds (s) to a nicely formatted and padded time string
   */
  getTimeText(s: number, cursor: boolean = true): string {
    let cursorText = ':';
    if (!cursor) {
      cursorText = ' ';
    }
    const m: number = Math.floor(s / 60);
    const minutes = '0' + m;
    const seconds = '0' + (s - m * 60);
    return minutes.substr(-2) + cursorText + seconds.substr(-2);
  }

  hideMessage(duration: number = 2000) {
    if (duration === 0) {
      this.message.text = undefined;
      this.message.alpha = 0;
      this.message.isVisible = false;
    } else {
      setTimeout(() => {
        this.gui2D.fadeOut(this.message, () => {
          this.message.text = undefined;
        });
      }, duration);
    }
  }

  showError(message: string, duration: number = 2000, big: boolean = false) {
    return this.showMessage(message, duration, big, 'red');
  }

  showMessage(message: string, duration: number = 2000, big: boolean = false, color: string = Gui.primaryColor.toHexString()) {
    this.message.text = message;

    if (big) {
      this.setBigMessageStyle();
    }

    if (duration === 0) {
      this.message.alpha = 1;
      this.message.isVisible = true;
      return;
    }

    this.message.color = color;

    this.gui2D.fadeIn(this.message);
    setTimeout(() => {
      this.gui2D.fadeOut(this.message, () => {
        this.message.text = undefined;
        this.setSmallMessageStyle();
      });
    }, duration);
  }

  createUiLabel(mesh: TransformNode, text: string, linkOffset: Vector2): Rectangle {
    const rect = new Rectangle();
    rect.width = 0.2;
    rect.height = '40px';
    rect.cornerRadius = 20;
    rect.color = 'Orange';
    rect.thickness = 4;
    rect.background = 'green';
    this.gui2D.ui.addControl(rect);

    var label = new TextBlock();
    label.text = text;
    rect.addControl(label);

    rect.linkWithMesh(mesh);
    rect.linkOffsetX = linkOffset.x;
    rect.linkOffsetY = linkOffset.y;
    return rect;
  }

  createLabel2(mesh: TransformNode, text: string): AdvancedDynamicTexture {
    // GUI
    const plane = MeshBuilder.CreatePlane('plane', { size: 2 });
    plane.parent = mesh;
    plane.position = mesh.position.addInPlace(new Vector3(0, 5, 0));
    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;

    const advancedTexture = AdvancedDynamicTexture.CreateForMesh(plane);

    const button = Button.CreateSimpleButton('but1', text);
    button.width = 4;
    button.height = 2;
    button.color = 'white';
    button.fontSize = 150;
    button.background = 'green';
    advancedTexture.addControl(button);

    return advancedTexture;
  }

  createUISign(mesh: TransformNode, text: string): Control[] {
    const rect = new Rectangle();
    rect.width = 0.2;
    rect.height = '40px';
    rect.cornerRadius = 20;
    rect.color = 'Orange';
    rect.thickness = 4;
    rect.background = 'green';
    this.gui2D.ui.addControl(rect);
    rect.linkWithMesh(mesh);
    rect.linkOffsetY = -150;
    const label = new TextBlock();
    label.text = text;
    rect.addControl(label);

    const target = new Ellipse();
    target.width = '40px';
    target.height = '40px';
    target.color = 'Orange';
    target.thickness = 4;
    target.background = 'green';
    this.gui2D.ui.addControl(target);
    target.linkWithMesh(mesh);

    const line = new Line();
    line.lineWidth = 4;
    line.color = 'Orange';
    line.y2 = 20;
    line.linkOffsetY = -20;
    this.gui2D.ui.addControl(line);
    line.linkWithMesh(mesh);
    line.connectedControl = rect;

    return [rect, label, target, line];
  }

  dispose() {
    this.gui2D.ui.dispose();
  }

  /**
   * Pre-create all animation pool controls once and add them to the GUI (hidden).
   */
  private initAnimPool(): void {
    for (let i = 0; i < Hud.MAX_CONCURRENT_ANIMS; i++) {
      const ctrl = new TextBlock(`collectible_anim_${i}`, '');
      ctrl.fontSize = '48px';
      ctrl.widthInPixels = 64;
      ctrl.heightInPixels = 64;
      ctrl.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      ctrl.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
      ctrl.isVisible = false;
      ctrl.alpha = 1;
      this.gui2D.ui.addControl(ctrl);
      this.animPool.push(ctrl);
      this.animPoolInUse.push(false);
    }
  }

  animateCollection(startPosition: Vector3, icon: string, targetControlName: string, onComplete: () => void) {
    // Claim a free pool slot — if all are busy, fire onComplete immediately so stats are never lost
    const slot = this.animPoolInUse.indexOf(false);
    if (slot === -1) {
      onComplete();
      return;
    }
    this.animPoolInUse[slot] = true;

    const engine = this.level.game.engine;
    const scene = this.level.game.scene;

    // Project 3D → 2D.  Reuse cached identity matrix — avoids a Matrix allocation per call.
    const uiSize = this.gui2D.ui.getSize();
    const vector = Vector3.Project(
      startPosition,
      this.identityMatrix,
      scene.getTransformMatrix(),
      scene.activeCamera.viewport.toGlobal(uiSize.width, uiSize.height)
    );

    // Reconfigure the pooled control for this animation
    const iconControl = this.animPool[slot];
    iconControl.text = icon;
    iconControl.leftInPixels = vector.x;
    iconControl.topInPixels = vector.y;
    iconControl.alpha = 0.7;
    iconControl.isVisible = true;

    // Resolve target HUD control position
    const targetControl: Control = (this as any)[targetControlName];
    let targetX: number;
    let targetY: number;

    if (!targetControl) {
      iconControl.isVisible = false;
      this.animPoolInUse[slot] = false;
      onComplete();
      return;
    }

    const measure = (targetControl as any)._currentMeasure;
    if (measure) {
      targetX = measure.left + measure.width / 2;
      targetY = measure.top + measure.height / 2;
      if (targetControl.parent && (targetControl.parent as any)._currentMeasure) {
        const parentMeasure = (targetControl.parent as any)._currentMeasure;
        targetX += parentMeasure.left;
        targetY += parentMeasure.top;
      }
    } else {
      targetX = engine.getRenderWidth() * 0.1;
      targetY = engine.getRenderHeight() * 0.05;
    }

    const startTime = performance.now();
    const duration = 800;
    const startX = vector.x;
    const startY = vector.y;

    // Ease method (Quadratic ease in-out)
    const ease = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

    const observer = scene.onBeforeRenderObservable.add(() => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const t = ease(progress);

      iconControl.leftInPixels = startX + (targetX - startX) * t;
      iconControl.topInPixels = startY + (targetY - startY) * t;
      iconControl.alpha = 0.7 - t * 0.5;

      if (progress >= 1) {
        scene.onBeforeRenderObservable.remove(observer);
        // Return slot to pool — just hide, do NOT dispose or remove from GUI
        iconControl.isVisible = false;
        this.animPoolInUse[slot] = false;
        onComplete();
      }
    });
  }
}
